import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent, MealType, PredictedMeal } from '@demind-inc/core';
import dayjs from 'dayjs';
import { metricsApi } from '../api';
import { useRecoilState } from 'recoil';
import { eventsSnackBarAtom } from '../recoil';

interface UseUpdateMealPredictionParams {
  metricId: string;
  date: string;
  prediction: PredictedMeal;
  shouldUpdateEvent?: boolean;
  editedManually?: boolean;
  baseCalendarId?: string;
  chatSessionId?: string;
}

export function useUpdateMealPrediction() {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const updateMealPredictionMutation = useMutation({
    mutationFn: ({
      metricId,
      date,
      prediction,
      shouldUpdateEvent,
      editedManually,
      baseCalendarId,
      chatSessionId,
    }: UseUpdateMealPredictionParams) => {
      return metricsApi.updateMealPrediction(metricId, {
        date,
        prediction,
        shouldUpdateEvent,
        editedManually,
        baseCalendarId,
        chatSessionId,
      });
    },
    onMutate: async ({ date, prediction }) => {
      const mealType = Object.keys(prediction)[0] as MealType;
      const mealPrediction = prediction[mealType];

      if (!mealPrediction) return {};

      setEventsSnackbar({ message: 'Event updating...', type: 'loading' });

      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });

      const queries = queryClient.getQueriesData({ queryKey: ['lifestack.calendar.events'] });
      const previousQueries: [QueryKey, CalendarEvent[]][] = [];

      queries.forEach(([queryKey, data]) => {
        if (data && Array.isArray(data)) {
          previousQueries.push([queryKey, data]);

          const updatedData = data.map((event: CalendarEvent) => {
            const isMealEvent = event.autoScheduleFrom?.from === 'meal';
            if (!isMealEvent) return event;

            if (!dayjs(event.start?.date).isSame(date, 'day')) return event;

            const eventName = event.summary?.toLowerCase();
            if (!eventName?.includes(mealType)) return event;

            return {
              ...event,
              start: {
                ...event.start,
                date: dayjs(date).format('YYYY-MM-DD') + 'T' + mealPrediction.startTime + ':00',
              },
              end: {
                ...event.end,
                date: dayjs(date).format('YYYY-MM-DD') + 'T' + mealPrediction.endTime + ':00',
              },
              description: mealPrediction.description,
            };
          });

          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousQueries };
    },
    onSuccess: (_, { prediction }) => {
      const mealType = Object.keys(prediction)[0] as MealType;
      const mealPrediction = prediction[mealType];

      if (!mealPrediction) return {};

      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      setEventsSnackbar({ message: 'Event updated', type: 'success' });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });

  return {
    updateMealPrediction: updateMealPredictionMutation.mutateAsync,
    ...updateMealPredictionMutation,
  };
}
